/**
 * TenantAutoBillingConfig — per-tenant auto-billing rules for Housing + Dining.
 *
 * When enabled:
 *  • Housing allocation → pending invoice line (room-type specific rate fallback to default)
 *  • Meal log          → pending invoice line (plan.price_per_meal, else per-meal rate, else default)
 *
 * Rules are stored at school_settings.auto_billing.{housing|dining}.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Receipt, Save } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TenantAutoBillingConfig = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    housing: { enabled: false, default_rate: 0, rates: { ensuite: 0, dormitory: 0 } },
    dining: { enabled: false, default_per_meal: 0 },
  });

  useEffect(() => {
    if (!tenantId) return;
    axios.get(`${API_URL}/api/school/tenants/${tenantId}/settings/section/auto_billing`, { headers: authHeaders() })
      .then((r) => {
        if (r.data && typeof r.data === 'object') {
          setForm((prev) => ({
            housing: { ...prev.housing, ...(r.data.housing || {}), rates: { ...prev.housing.rates, ...((r.data.housing || {}).rates || {}) } },
            dining: { ...prev.dining, ...(r.data.dining || {}) },
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenantId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/school/tenants/${tenantId}/settings/section/auto_billing`,
        {
          housing: { ...form.housing, default_rate: Number(form.housing.default_rate) || 0, rates: Object.fromEntries(Object.entries(form.housing.rates).map(([k, v]) => [k, Number(v) || 0])) },
          dining: { ...form.dining, default_per_meal: Number(form.dining.default_per_meal) || 0 },
        },
        { headers: authHeaders() },
      );
      toast.success('Auto-billing rules saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <Card className="border-slate-200" data-testid="tenant-autobilling-config-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Receipt className="h-4 w-4 text-indigo-500" /> Auto-Billing Rules</CardTitle>
        <CardDescription>
          Automatically create pending invoice lines when students use housing or dining services. Leave rates at 0 to skip.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Housing */}
        <div className="space-y-2 pb-3 border-b">
          <label className="flex items-center gap-2">
            <Switch checked={!!form.housing.enabled} onCheckedChange={(v) => setForm({ ...form, housing: { ...form.housing, enabled: !!v } })} data-testid="autobill-housing-enabled" />
            <span className="text-sm font-medium">Housing — monthly boarding fee on allocation</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-10">
            <div><Label className="text-xs">Default rate / month</Label><Input type="number" value={form.housing.default_rate} onChange={(e) => setForm({ ...form, housing: { ...form.housing, default_rate: e.target.value } })} data-testid="autobill-housing-default" /></div>
            <div><Label className="text-xs">Ensuite rate</Label><Input type="number" value={form.housing.rates.ensuite} onChange={(e) => setForm({ ...form, housing: { ...form.housing, rates: { ...form.housing.rates, ensuite: e.target.value } } })} /></div>
            <div><Label className="text-xs">Dormitory rate</Label><Input type="number" value={form.housing.rates.dormitory} onChange={(e) => setForm({ ...form, housing: { ...form.housing, rates: { ...form.housing.rates, dormitory: e.target.value } } })} /></div>
          </div>
        </div>
        {/* Dining */}
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <Switch checked={!!form.dining.enabled} onCheckedChange={(v) => setForm({ ...form, dining: { ...form.dining, enabled: !!v } })} data-testid="autobill-dining-enabled" />
            <span className="text-sm font-medium">Dining — per-meal fee on meal log</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-10">
            <div><Label className="text-xs">Default per meal</Label><Input type="number" step="0.01" value={form.dining.default_per_meal} onChange={(e) => setForm({ ...form, dining: { ...form.dining, default_per_meal: e.target.value } })} data-testid="autobill-dining-default" /></div>
          </div>
          <p className="text-[11px] text-slate-500 pl-10">Meal plans can override this with a <code>price_per_meal</code> field.</p>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || loading} data-testid="autobill-save-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TenantAutoBillingConfig;
